<template>
  <div>
    <svg
      :class="bem('svg', { small: true })"
      width="100%"
      preserveAspectRatio="none"
      viewBox="0 0 670 687"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        ref="mobilePathOne"
        :class="bem('line', { one: true })"
        d="M0 551.501C57 551.501 222.488 956 260.892 336.418C299.296 -283.164 389.5 155 669.5 170"
        stroke="#878787"
        stroke-width="10"
      />
      <path
        ref="mobilePathTwo"
        :class="bem('line', { two: true})"
        d="M0 551.501C57 551.501 222.488 956 260.892 336.418C291.848 -163.002 356.46 24.8298 527 124.326"
        stroke="#FD5102"
        stroke-width="10"
      />
      <circle
        ref="mobileDot"
        r="10"
        fill="#FD5102"
        :class="bem('dot')"
      >
        <animateMotion
          ref="mobileDotMotion"
          dur="2s"
          begin="indefinite"
          repeatCount="0"
          calcMode="spline"
          keySplines="0.42, 0, 0.58, 1"
          fill="freeze"
          path="M0 551.501C57 551.501 222.488 956 260.892 336.418C291.848 -163.002 356.46 24.8298 527 124.326"
        />
      </circle>
    </svg>

    <svg
      :class="bem('svg', { large: true })"
      width="100%"
      preserveAspectRatio="none"
      viewBox="0 0 2001 606"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        ref="pathOne"
        d="M1 398.5C80 396 475 877.5 789.5 363.241C1104 -151.018 1383.5 -28.3259 1554 222.92C1724.5 474.166 1824.5 351.5 2000.5 359.5"
        stroke="#878787"
        stroke-width="10"
        :class="bem('line', { one: true })"
        vector-effect="non-scaling-stroke"
      />
      <path
        ref="pathTwo"
        d="M1 398.5C80 396 475 877.5 789.5 363.241C1104 -151.018 1383.5 -28.3259 1554 222.92"
        stroke="#FD5102"
        stroke-width="10"
        :class="bem('line', { two: true })"
        vector-effect="non-scaling-stroke"
      />
      <circle
        ref="dot"
        r="15"
        fill="#FD5102"
        :class="bem('dot')"
      >
        <animateMotion
          ref="dotMotion"
          dur="2s"
          begin="indefinite"
          repeatCount="0"
          calcMode="spline"
          keySplines="0.42, 0, 0.58, 1"
          fill="freeze"
          path="M1 398.5C80 396 475 877.5 789.5 363.241C1104 -151.018 1383.5 -28.3259 1554 222.92"
        />
      </circle>
    </svg>
  </div>
</template>

<script setup>
const bem = useBEM('StravaPath')
const { isMdBreakpointOrAbove } = useBreakpoint()

const pathOne = ref()
const pathTwo = ref()
const dot = ref()
const dotMotion = ref()
const observer = ref(null)
const mobilePathOne = ref()
const mobilePathTwo = ref()
const mobileDot = ref()
const mobileDotMotion = ref()
const desktopAnimationPlayed = ref(false)
const mobileAnimationPlayed = ref(false)

function buildSvgPathOffset(path, delay = '0s') {
  const length = path.getTotalLength();
  path.style.transition = path.style.WebkitTransition = 'none';
  path.style.strokeDasharray = length + ' ' + length;
  path.style.strokeDashoffset = length;
  path.getBoundingClientRect();
  path.style.transition = path.style.WebkitTransition =
    `stroke-dashoffset 2s ${delay} ease-in-out`;
  path.style.opacity = 1
  path.style.strokeDashoffset = '0';
}

onMounted(() => {
  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && isMdBreakpointOrAbove.value) {
          buildSvgPathOffset(pathOne.value)
          buildSvgPathOffset(pathTwo.value, '1s')
        } else if (entry.isIntersecting) {
          buildSvgPathOffset(mobilePathOne.value)
          buildSvgPathOffset(mobilePathTwo.value, '1s')
        }
      })
    }
  )

  observer.value.observe(pathOne.value)
  observer.value.observe(pathTwo.value)
  observer.value.observe(mobilePathOne.value)
  observer.value.observe(mobilePathTwo.value)

  pathTwo.value.addEventListener('transitionstart', () => {
    dotMotion.value.beginElement()
    dot.value.style.opacity = '1'
  })

  pathTwo.value.addEventListener('transitionend', () => {
    desktopAnimationPlayed.value = true
    observer.value.unobserve(pathOne.value)
    observer.value.unobserve(pathTwo.value)
  })

  mobilePathTwo.value.addEventListener('transitionstart', () => {
    mobileDotMotion.value.beginElement()
    mobileDot.value.style.opacity = '1'
  })

  mobilePathTwo.value.addEventListener('transitionend', () => {
    mobileAnimationPlayed.value = true
    observer.value.unobserve(mobilePathOne.value)
    observer.value.unobserve(mobilePathTwo.value)
  })
})

watch(() => isMdBreakpointOrAbove.value, (newValue) => {
  if (newValue && !desktopAnimationPlayed.value) {
    buildSvgPathOffset(pathOne.value)
    buildSvgPathOffset(pathTwo.value, '1s')
  } else if(!newValue && !mobileAnimationPlayed.value) {
    buildSvgPathOffset(mobilePathOne.value)
    buildSvgPathOffset(mobilePathTwo.value, '1s')
  }
})

onBeforeUnmount(() => {
  observer.value.disconnect()
})
</script>

<style lang="postcss" scoped>
.StravaPath {
  @apply w-full;
}

.StravaPath__svg--small {
  @screen md {
    @apply hidden;
  }
}

.StravaPath__svg--large {
  @apply hidden;

  @screen md {
    @apply block;
  }
}

.StravaPath__svg {
  width: 100vw;
}

.StravaPath__line,
.StravaPath__dot {
  @apply opacity-0 transition-opacity duration-1000;
}
</style>